import debounce from "lodash-es/debounce";
import {ChevronDown, Loader2, SearchIcon, X} from "lucide-react";
import {useMemo, useState, type UIEvent} from "react";

import {Button} from "@/components/ui/button";
import {
  Combobox,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import {cn} from "@/lib/utils";
import {ParamsOptions} from "@/src/modules/core/types";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useInfinitePropertiesWithDispatch} from "@/src/modules/properties/hooks/use-infinite-properties-with-dispatch";
import {formatPropertyAddressLine} from "@/src/modules/properties/lib/property-address";
import type {Property} from "@/src/modules/properties/types";
import {shiftsService} from "@/src/modules/shifts/services";

import {
  demoteFormPropertyPrimary,
  mergeComboboxProperties,
  removeFormProperty,
  setFormPropertyPrimary,
  sortFormPropertiesForDisplay,
  type ShiftFormProperty,
} from "../../lib/shift-property-form";

const EMPTY_FILTER: ParamsOptions["filter"] = {};

type ShiftPropertyAssignmentsFieldProps = {
  value: ShiftFormProperty[];
  onChange: (value: ShiftFormProperty[]) => void;
  disabled?: boolean;
  filter?: ParamsOptions["filter"];
  placeholder?: string;
  className?: string;
};

export function ShiftPropertyAssignmentsField({
  value,
  onChange,
  disabled = false,
  filter = EMPTY_FILTER,
  placeholder = "Select properties",
  className,
}: ShiftPropertyAssignmentsFieldProps) {
  const me = useStoredMe()!;
  const anchor = useComboboxAnchor();
  const [nameSearch, setNameSearch] = useState("");

  const filterByUserProperties = shiftsService.getRoleMustFilterByUserProperties();

  const propertiesFilter = useMemo(() => {
    const next: Record<string, unknown> = {...filter};

    if (filterByUserProperties) {
      next.ids = me.user_properties.map((property) => property.id).join(",");
    }

    const trimmed = nameSearch.trim();
    if (trimmed) {
      next.name = trimmed;
    }

    return next;
  }, [filter, filterByUserProperties, me.user_properties, nameSearch]);

  const query = useInfinitePropertiesWithDispatch(
    {sort: "name", filter: propertiesFilter},
    {mergeDispatchSites: filterByUserProperties},
  );
  const {properties, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage} = query;

  const assignedIds = useMemo(() => new Set(value.map((row) => row.id)), [value]);

  const comboboxValue = useMemo(
    () => value.map((row) => ({id: row.id, name: row.name})),
    [value],
  );

  const comboboxItems = useMemo(() => {
    const assignedStubs = value.map(
      (row) => ({id: row.id, name: row.name}) as Property,
    );
    const merged = new Map<number, Property>();
    for (const property of assignedStubs) {
      merged.set(property.id, property);
    }
    for (const property of properties) {
      if (!merged.has(property.id)) {
        merged.set(property.id, property);
      }
    }
    return [...merged.values()];
  }, [properties, value]);

  const availableProperties = useMemo(
    () => comboboxItems.filter((property) => !assignedIds.has(property.id)),
    [comboboxItems, assignedIds],
  );

  const handleDebounceSearch = useMemo(
    () =>
      debounce((term: string) => {
        setNameSearch(term);
      }, 300),
    [],
  );

  const assignedProperties = useMemo(() => sortFormPropertiesForDisplay(value), [value]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return;
    const element = event.currentTarget;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 8) {
      void fetchNextPage();
    }
  };

  return (
    <Combobox
        multiple
        items={comboboxItems}
        value={comboboxValue}
        disabled={disabled}
        itemToStringLabel={(item: unknown) => (item as Property).name}
        isItemEqualToValue={(item: unknown, selected: unknown) =>
          (item as Property).id === (selected as Property | null)?.id
        }
        onValueChange={(nextValue: unknown) => {
          if (disabled) return;
          const selected = (nextValue as Property[] | null) ?? [];
          onChange(mergeComboboxProperties(value, selected));
        }}
        onInputValueChange={(inputValue, event) => {
          if (disabled) return;
          const reason = event.reason as string;
          if (reason !== "input-change" && reason !== "input-clear" && reason !== "input-paste") {
            return;
          }
          if (inputValue.trim().length === 0) {
            handleDebounceSearch.cancel();
            setNameSearch("");
            return;
          }
          handleDebounceSearch(inputValue);
        }}
      >
        <ComboboxChips ref={anchor} className={cn("w-full", className)}>
          <SearchIcon className="text-muted-foreground size-4 shrink-0" />
          <ComboboxValue>
            {() => (
              <>
                <ComboboxChipsInput placeholder={placeholder} disabled={disabled} />
                {isFetching ? (
                  <Loader2 className="text-muted-foreground size-4 animate-spin" />
                ) : disabled ? null : (
                  <ChevronDown className="text-muted-foreground size-4" />
                )}
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>

        <ComboboxContent
          anchor={anchor}
          className="max-h-[min(30rem,max(16rem,var(--available-height)))]"
        >
          {assignedProperties.length > 0 ? (
            <div className="border-border max-h-48 shrink-0 overflow-y-auto border-b px-2 py-2">
              <p className="text-muted-foreground px-1 text-xs font-medium">Assigned</p>
              {assignedProperties.map((row) => (
              <div
                key={`assigned-${row.id}`}
                className="flex min-w-0 items-center gap-2 rounded-md px-1 py-1"
              >
                <span className="min-w-0 flex-1 truncate text-sm">{row.name}</span>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant={row.assignment_type === "primary" ? "default" : "outline"}
                    className="h-7 px-2 text-xs"
                    disabled={disabled}
                    onClick={() => onChange(setFormPropertyPrimary(value, row.id))}
                  >
                    Primary
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={row.assignment_type === "secondary" ? "default" : "outline"}
                    className="h-7 px-2 text-xs"
                    disabled={disabled || value.length === 1 || row.assignment_type === "secondary"}
                    onClick={() => onChange(demoteFormPropertyPrimary(value, row.id))}
                  >
                    Secondary
                  </Button>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-7 shrink-0"
                  disabled={disabled}
                  aria-label={`Remove ${row.name}`}
                  onClick={() => onChange(removeFormProperty(value, row.id))}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        {assignedProperties.length > 0 ? (
          <p className="text-muted-foreground shrink-0 px-3 pt-2 text-xs font-medium">
            Add property
          </p>
        ) : null}

        {!isFetching && !isFetchingNextPage && availableProperties.length === 0 ? (
          <ComboboxEmpty>No results found.</ComboboxEmpty>
        ) : null}

        <ComboboxList className="min-h-24 flex-1" onScroll={handleScroll}>
          {availableProperties.map((property) => (
            <ComboboxItem key={property.id} value={property}>
              <Item size="sm" className="p-0">
                <ItemContent>
                  <ItemTitle>{property.name}</ItemTitle>
                  <ItemDescription className="text-xs">
                    {formatPropertyAddressLine(property)}
                  </ItemDescription>
                </ItemContent>
              </Item>
            </ComboboxItem>
          ))}
        </ComboboxList>

        {isFetching ? (
          <Item className="shrink-0">
            <ItemContent>
              <ItemDescription className="flex items-center gap-2 text-xs">
                {isFetchingNextPage ? "Loading more properties…" : "Loading properties…"}
                <Loader2 className="size-4 animate-spin" />
              </ItemDescription>
            </ItemContent>
          </Item>
        ) : null}
      </ComboboxContent>
    </Combobox>
  );
}
